// src/components/coupon/ProductSelectModal.tsx
import * as React from "react";
import useSWR from "swr";
import { Button, Input, Modal, Pagination, Space, Spin, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { axiosInstance } from "src/api/axiosInstance";

export type SelectedProduct = {
  id: number;
  name: string;
};

type Props = {
  open: boolean;
  selectedIds: number[];
  selectedItems?: SelectedProduct[];
  onClose: () => void;
  onConfirm: (selectedItems: SelectedProduct[]) => void;
};

interface ProductItem {
  productId: number;
  productName: string;
  productCode?: string;
  brandName?: string;
}

interface ProductListResponse {
  content: ProductItem[];
  page: {
    size: number;
    number: number;
    totalElements: number;
    totalPages: number;
  };
}

const getProductListApi = async (params: {
  productName?: string;
  page: number;
  size: number;
  order?: string;
}) => {
  const res = await axiosInstance.get<{ data: ProductListResponse }>(
    "/v1/admin/baseinfo/product/list",
    {
      params,
      snackbar: { success: false },
    }
  );
  return res.data;
};

export default function ProductSelectModal({
  open,
  selectedIds,
  selectedItems,
  onClose,
  onConfirm,
}: Props) {
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [size] = React.useState(10);
  const [tempSelected, setTempSelected] = React.useState<number[]>([]);

  React.useEffect(() => {
    if (open) {
      setTempSelected([...selectedIds]);
      setSearch("");
      setPage(1);
    }
  }, [open, selectedIds]);

  const { data, isLoading } = useSWR(
    open ? ["product/list/modal", search, page, size] : null,
    async () => {
      const apiPage = Math.max(0, page - 1);
      return await getProductListApi({
        productName: search.trim() || undefined,
        page: apiPage,
        size: size,
        order: "createdAt,desc",
      });
    },
    { revalidateOnFocus: false }
  );

  const list = data?.data?.content ?? [];
  const totalElements = data?.data?.page?.totalElements ?? 0;

  const handleConfirm = () => {
    const result: SelectedProduct[] = tempSelected.map((id) => {
      const product = list.find((item) => item.productId === id);
      if (product) {
        return { id: product.productId, name: product.productName || `상품 ${product.productId}` };
      }
      const prevSelected = selectedItems?.find((item) => item.id === id);
      if (prevSelected) return prevSelected;
      return { id, name: `상품 ${id}` };
    });
    onConfirm(result);
    onClose();
  };

  const columns: ColumnsType<ProductItem> = [
    { title: "ID", dataIndex: "productId", width: 80 },
    { title: "상품명", dataIndex: "productName" },
    { title: "상품코드", dataIndex: "productCode", width: 120 },
    { title: "브랜드", dataIndex: "brandName", width: 120 },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="상품 선택"
      width={900}
      centered
      footer={
        <Space>
          <Button onClick={onClose}>취소</Button>
          <Button type="primary" onClick={handleConfirm}>
            선택
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="상품명 검색"
        />

        <Typography.Text type="secondary">선택된 상품: {tempSelected.length}개</Typography.Text>

        {isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
            <Spin />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={list}
            rowKey="productId"
            pagination={false}
            scroll={{ x: 1000 }}
            rowSelection={{
              type: "checkbox",
              selectedRowKeys: tempSelected,
              onChange: (selectedRowKeys) => {
                setTempSelected(selectedRowKeys.map(k => Number(k)));
              },
            }}
            onRow={(record) => ({
              onClick: () => {
                setTempSelected((prev) =>
                  prev.includes(record.productId)
                    ? prev.filter((id) => id !== record.productId)
                    : [...prev, record.productId]
                );
              },
            })}
          />
        )}

        <div style={{ display: "flex", justifyContent: "center" }}>
          <Pagination
            current={page}
            total={Math.max(1, totalElements)}
            pageSize={size}
            onChange={(nextPage) => setPage(nextPage)}
            showSizeChanger={false}
          />
        </div>
      </Space>
    </Modal>
  );
}
